import {apiClient} from '@/shared/services/apiClient';
import type {
    OrderTestsCatalogResponse,
    OrderTestsPatientSearchItem,
    OrderTestsQueuedPatient,
    OrderTestsSpecimenType,
    OrderTestsCollector,
    OrderTestsDraftOrder,
    OrderTestsSaveResult,
    OrderDraftData,
} from '@/shared/types/api';

export interface SaveOrderRequest {
    elisOrderId: number;
    dashboardUserId?: number;
    patient: {
        elisPatientId?: number;
        firstName: string;
        lastName: string;
        birthDate: string;
        gender: string;
    };
    selectedTestIds: number[];
    selectedElisPanelIds?: number[];
    collectionSpecimenTypeID: number;
    additionalSpecimens?: {id: number; name: string}[];
    collector: {
        elisUserId: number;
        name: string;
    };
    collectionDate: string;
    collectionTime: string;
    /** Optional free-text note from the ordering provider. Sent to ELIS as DoctorOrderNote. */
    notes?: string;
}

export const ProviderOrderTestsService = {
    // Queue
    async getQueue(): Promise<{queue: OrderTestsQueuedPatient[]}> {
        return apiClient.get<{queue: OrderTestsQueuedPatient[]}>('/api/provider/order-tests/queue');
    },

    async addToQueue(userId: number, notes?: string): Promise<{ok: boolean}> {
        return apiClient.post<{ok: boolean}>('/api/provider/order-tests/queue', {userId, notes});
    },

    async createPatientAndQueue(patient: {
        firstName: string;
        lastName: string;
        email: string;
        birthDate: string;
        gender: string;
        elisPatientId?: number;
    }): Promise<{ok: boolean; userId: number}> {
        return apiClient.post<{ok: boolean; userId: number}>('/api/provider/order-tests/queue/create-patient', patient);
    },

    async removeFromQueue(userId: number): Promise<{ok: boolean}> {
        return apiClient.delete<{ok: boolean}>(`/api/provider/order-tests/queue/${userId}`);
    },

    // Catalog
    async getCatalog(): Promise<OrderTestsCatalogResponse> {
        return apiClient.get<OrderTestsCatalogResponse>('/api/provider/order-tests/catalog');
    },

    // Patient search
    async searchPatients(search: string): Promise<{patients: OrderTestsPatientSearchItem[]}> {
        return apiClient.get<{patients: OrderTestsPatientSearchItem[]}>('/api/provider/order-tests/patients', {
            params: {search},
        });
    },

    // Reference data
    async getSpecimenTypes(): Promise<{specimenTypes: OrderTestsSpecimenType[]}> {
        return apiClient.get<{specimenTypes: OrderTestsSpecimenType[]}>('/api/provider/order-tests/specimen-types');
    },

    async getCollectors(): Promise<{collectors: OrderTestsCollector[]}> {
        return apiClient.get<{collectors: OrderTestsCollector[]}>('/api/provider/order-tests/collectors');
    },

    // Order lifecycle
    async createDraftOrder(): Promise<OrderTestsDraftOrder> {
        return apiClient.post<OrderTestsDraftOrder>('/api/provider/order-tests');
    },

    async saveOrderDraft(data: SaveOrderRequest): Promise<OrderTestsSaveResult> {
        return apiClient.put<OrderTestsSaveResult>(`/api/provider/order-tests/${data.elisOrderId}`, data);
    },

    async submitOrder(data: SaveOrderRequest): Promise<OrderTestsSaveResult> {
        return apiClient.post<OrderTestsSaveResult>(`/api/provider/order-tests/${data.elisOrderId}/submit`, data);
    },

    // Draft data persistence
    async getDraft(userId: number): Promise<{draft: OrderDraftData | null}> {
        return apiClient.get<{draft: OrderDraftData | null}>(`/api/provider/order-tests/queue/${userId}/draft`);
    },

    async saveDraft(userId: number, data: OrderDraftData, saveToElis = false): Promise<{ok: boolean; elisError?: boolean}> {
        const params = saveToElis ? '?saveToElis=1' : '';
        return apiClient.put<{ok: boolean; elisError?: boolean}>(`/api/provider/order-tests/queue/${userId}/draft${params}`, data);
    },
};
